import {groupBy, sortBy, union} from 'lodash';
import equalId from 'src/utils/equalId';
import filterDeleted from 'src/utils/filterDeleted';
import {getNumber, getPriceNumber, isNumber, sumBy} from 'src/utils/numberUtils';

const NO_VARIATION = 'none';

const getVariationKey = (item) => item.variation?.id ?? item.variation ?? NO_VARIATION;

const getTime = (item) => new Date(item.created_at).getTime() || 0;

const getStockQuantity = (stock) => Math.max(getNumber(stock.quantity), 0);

// average cost per unit inside a total cost, null when there's no quantity
export const getUnitCost = (total, quantity) => (quantity > 0 ? getPriceNumber(total / quantity) : null);

// stock rows that belong to the selected branch and variation
const getStocks = (product, {branch, variationId}) => {
	const variations = filterDeleted(product?.variations ?? []);

	return (product?.stocks ?? []).filter((stock) => {
		if (branch && !equalId(stock.branch, branch)) return false;
		if (variationId) return equalId(stock.variation, variationId);
		// skip leftover rows: product level row on variable product, or row of deleted variation
		if (variations.length > 0) return variations.some((v) => equalId(v, stock.variation));
		return !stock.variation;
	});
};

// cost of the latest purchase recorded before this time, or the first purchase when nothing was purchased yet
// purchases must be sorted by time
const getKnownCost = (purchases = [], time) => {
	const before = purchases.filter((p) => getTime(p) <= time);
	const purchase = before[before.length - 1] ?? purchases[0];
	return purchase ? getNumber(purchase.price) : null;
};

/**
 * first in first out: each sale takes its cost from the oldest purchase that still has quantity,
 * so the cost of a sale never changes when a new purchase is added later
 *
 * @returns {{saleCosts: array, layers: array, getFallbackCost: function}} layers are purchases not sold yet
 */
const runFifo = (purchases = [], sales = [], productPurchases = []) => {
	const getFallbackCost = (time) => getKnownCost(purchases, time) ?? getKnownCost(productPurchases, time);
	const layers = [];
	const saleCosts = [];

	// on same time purchase comes first
	sortBy([...purchases, ...sales], [getTime, (event) => event.type !== 'purchase']).forEach((event) => {
		if (event.type === 'purchase') {
			layers.push({quantity: getNumber(event.quantity), cost: getNumber(event.price)});
			return;
		}

		let remaining = getNumber(event.quantity);
		let cost = 0;
		while (remaining > 0 && layers.length > 0) {
			const taken = Math.min(remaining, layers[0].quantity);
			cost += taken * layers[0].cost;
			layers[0].quantity -= taken;
			remaining -= taken;
			if (layers[0].quantity <= 0) layers.shift();
		}
		// sold stock that has no purchase (ex: added by quantity update): use last known cost
		const isEstimated = remaining > 0;
		if (isEstimated) cost += remaining * getFallbackCost(getTime(event));

		saleCosts.push({sale: event, cost, isEstimated});
	});

	return {saleCosts, layers, getFallbackCost};
};

/**
 * profit = sales - cost of sold items (first in first out)
 * unsold stock is not counted as loss, its cost is returned separately as stockValue
 *
 * @param {array} items raw inventory changes (order quantity is positive)
 * @param {{branch?: any, variationId?: any, product?: object}} filter
 */
export const getInventoryProfit = (items = [], {branch, variationId, product} = {}) => {
	const isSelected = (row) =>
		(!variationId || equalId(row.variation, variationId)) && (!branch || equalId(row.branch, branch));

	// purchases and orders are not filtered by branch: stock can be purchased in one branch then transferred to another
	const purchases = sortBy(
		items.filter((item) => item.type === 'purchase' && isNumber(item.price) && getNumber(item.quantity) > 0),
		getTime,
	);
	const orders = items.filter((item) => item.type === 'order');
	const sales = orders.filter(isSelected);
	const stocks = getStocks(product, {branch, variationId});

	const soldQuantity = sumBy(sales, (s) => s.quantity);
	const revenue = getPriceNumber(sumBy(sales, (s) => s.total));
	// infinite stock has no quantity to count
	const isInfinite = !!product?.infinite || stocks.some((s) => s.infinite);
	const stockQuantity = isInfinite ? null : sumBy(stocks, getStockQuantity);

	if (purchases.length === 0) {
		return {soldQuantity, revenue, cost: null, profit: null, stockQuantity, stockValue: null, isEstimated: false};
	}

	// each variation has its own purchases, so we calculate cost per variation
	const purchasesByVariation = groupBy(purchases, getVariationKey);
	const ordersByVariation = groupBy(orders, getVariationKey);
	const stocksByVariation = groupBy(stocks, getVariationKey);
	let cost = 0;
	let stockValue = 0;
	let isEstimated = false;

	union(Object.keys(groupBy(sales, getVariationKey)), Object.keys(stocksByVariation)).forEach((key) => {
		const {saleCosts, layers, getFallbackCost} = runFifo(purchasesByVariation[key], ordersByVariation[key], purchases);
		const selectedSaleCosts = saleCosts.filter(({sale}) => isSelected(sale));
		cost += sumBy(selectedSaleCosts, (s) => s.cost);

		// stock is valued by the cost of purchases not sold yet
		const quantity = isInfinite ? 0 : sumBy(stocksByVariation[key] ?? [], getStockQuantity);
		const layersQuantity = sumBy(layers, (l) => l.quantity);
		const unitCost =
			layersQuantity > 0 ? sumBy(layers, (l) => l.quantity * l.cost) / layersQuantity : getFallbackCost(Infinity);
		stockValue += quantity * unitCost;

		if (selectedSaleCosts.some((s) => s.isEstimated) || (quantity > 0 && layersQuantity === 0)) isEstimated = true;
	});

	return {
		soldQuantity,
		revenue,
		cost: getPriceNumber(cost),
		profit: getPriceNumber(revenue - cost),
		stockQuantity,
		stockValue: isInfinite ? null : getPriceNumber(stockValue),
		isEstimated,
	};
};
